import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import CardBack from './CardBack';
import DeckStack from './spreads/DeckStack';
import { FAN_ROLL_MS, FAN_FOLD_MS } from './flow/useReadingFlow';
import './CardFanSelector.css';

// How long the deck takes to come down from the step above
// (deckToCirclePosition, CardFanSelector.css), less a moment's overlap
const ARRIVE_MS = 1300;

// How far each card in the gathered pile sits from the one above it, so
// the pile shows an edge
const PILE_X = 0.03;
const PILE_Y = 0.08;
// A still pile only needs enough of its cards drawn to show that edge
const PILE_EVERY = 10;

/**
 * The deck laid out in a circle to pick from. `deck` is what is left of
 * it: a pick (`onPick`, with where in the fan the card was, so the spread
 * can fly it from there) takes the card out, leaving its place in the
 * circle empty.
 *
 * While `open`, the cards are rolled out round the circle. Closed, they
 * roll back to the top of it, and the pile then drops to the middle as the
 * whole selector folds down to the height of a card. Opening again unfolds
 * and rolls out whatever is left.
 *
 * `deckRef` is the deck in the step above. The fan first opens by carrying
 * that deck down to the top of the circle, so it starts a stack of its own
 * exactly over it; DeckSection lets go of the real one at the same moment.
 */
function CardFanSelector({ deck, numCards, pickedCount = 0, deckRef, onPick, open = true }) {
  const { t } = useTranslation();
  // The cards as they were laid round the circle. Each keeps its place
  // while the deck shrinks; they are only re-spaced when the fan reopens.
  const [laidOut, setLaidOut] = useState(deck);
  // closed: piled in the middle of the folded selector
  // gathered: piled at the top of the circle
  // open: rolled out round it
  const [stage, setStage] = useState('gathered');
  // What the cards are doing on the way to `stage`: 'roll' round the
  // circle, 'fold' to or from the middle, or nothing once they are there
  const [motion, setMotion] = useState(null);
  const [clickedOnce, setClickedOnce] = useState(null);
  const [deckAnimating, setDeckAnimating] = useState(true); // The deck coming down
  // The cards are built with the fan and kept out of sight until the deck
  // has come down, so building them does not hitch the start of the roll
  const [showCards, setShowCards] = useState(false);
  const arrived = useRef(false);
  const travellerRef = useRef(null);
  const deckNow = useRef(deck);
  deckNow.current = deck;

  // Start the travelling deck where the real one is, at its size. Measured
  // before the first paint; without a deck to start from, the stylesheet's
  // own starting place above the circle stands.
  useLayoutEffect(() => {
    const from = deckRef?.current;
    const traveller = travellerRef.current;
    if (!from || !traveller) return;
    const start = from.getBoundingClientRect();
    const home = traveller.parentElement.getBoundingClientRect();
    if (!start.width || !traveller.offsetWidth) return;
    traveller.style.setProperty('--from-x', `${start.left + start.width / 2 - (home.left + home.width / 2)}px`);
    traveller.style.setProperty('--from-y', `${start.top + start.height / 2 - (home.top + home.height / 2)}px`);
    traveller.style.setProperty('--from-scale', start.width / traveller.offsetWidth);
    // Only the deck's place as the fan opens matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opening and closing, each in two moves
  useEffect(() => {
    const timers = [];
    const after = (ms, step) => timers.push(setTimeout(step, ms));

    if (open) {
      // The first time, the pile is the deck arriving from above; after
      // that it is unfolded from the middle of the closed selector
      const wait = arrived.current ? FAN_FOLD_MS : ARRIVE_MS;
      if (arrived.current) {
        setLaidOut(deckNow.current);
        setMotion('fold');
        setStage('gathered');
      }
      after(wait, () => {
        arrived.current = true;
        setShowCards(true);
      });
      // A beat with the pile in place, so the roll starts from it
      after(wait + 60, () => {
        setMotion('roll');
        setStage('open');
      });
      after(wait + 200, () => setDeckAnimating(false));
      after(wait + 60 + FAN_ROLL_MS, () => setMotion(null));
    } else {
      setClickedOnce(null);
      setMotion('roll');
      setStage('gathered');
      after(FAN_ROLL_MS, () => {
        setMotion('fold');
        setStage('closed');
      });
      after(FAN_ROLL_MS + FAN_FOLD_MS, () => setMotion(null));
    }
    return () => timers.forEach(clearTimeout);
  }, [open]);

  const canPick = open && stage === 'open' && motion === null;

  const handleCardClick = (card, fanCard) => {
    if (!canPick) return;

    // First click - highlight the card
    if (clickedOnce !== card.id) {
      setClickedOnce(card.id);
      return;
    }

    // Second click - take the card; it lands in the spread below
    setClickedOnce(null);
    onPick(card, fanCard.getBoundingClientRect());
  };

  const remaining = new Set(deck);
  const angleStep = 360 / laidOut.length;
  const rolledOut = stage === 'open';
  // Piled and not on its way round the circle, most of the pile is unseen
  const piled = !rolledOut && motion !== 'roll';
  let rank = -1;

  return (
    <div
      className={`card-fan-selector is-${stage} ${motion ? `is-${motion}ing` : ''} ${showCards ? '' : 'is-waiting'}`}
      style={{ '--fan-roll': `${FAN_ROLL_MS}ms`, '--fan-fold': `${FAN_FOLD_MS}ms` }}
    >
      <div className="selection-header">
        <h3>{t('deck.selectHeading', { count: numCards })}</h3>
        <p className="selection-instructions">
          {pickedCount === 0 && t('deck.selectHint')}
          {pickedCount > 0 && pickedCount < numCards &&
            t('deck.selectedProgress', { selected: pickedCount, total: numCards })}
        </p>
      </div>

      <div className="card-fan-container">
        {/* The deck, carried down from the step above to become the circle */}
        {deckAnimating && (
          <div className="animated-deck" ref={travellerRef}>
            <DeckStack />
          </div>
        )}

        <div className="card-fan">
          {laidOut.map((card, index) => {
            // A card that has been picked has left the deck, and the fan
            if (!remaining.has(card)) return null;
            rank += 1;

            const angle = rolledOut ? angleStep * index : 0;
            const depth = rolledOut ? 0 : laidOut.length - 1 - index;
            const buried = piled && rank % PILE_EVERY !== 0;
            const clicked = canPick && clickedOnce === card.id;

            return (
              <div
                key={card.id}
                className={`fan-card ${clicked ? 'clicked-once' : ''} ${buried ? 'is-buried' : ''}`}
                style={{
                  // The same list of moves at every stage, so that going
                  // from one to the next turns the card round the circle
                  // rather than cutting straight across it
                  transform: `
                    rotate(${angle}deg)
                    translateY(calc(var(--fan-radius) * ${stage === 'closed' ? 0 : -1}))
                    rotate(${-angle}deg)
                    translate(${-depth * PILE_X}px, ${-depth * PILE_Y}px)
                  `,
                  '--z': laidOut.length - index
                }}
                onClick={(event) => handleCardClick(card, event.currentTarget)}
              >
                <div className="fan-card-inner">
                  <CardBack className="fan-card-image" />
                  {clicked && (
                    <div className="click-indicator">
                      {t('deck.clickAgain')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default CardFanSelector;
